export type JsonRecord = Record<string, unknown>;

export function toRecord(value: unknown): JsonRecord | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as JsonRecord;
}

export function readString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

/**
 * Resolve the first matching string value from a list of candidate keys.
 * Replaces the repeated `readString(r.x) ?? readString(r.y) ?? ...` pattern.
 */
export function resolveString(record: JsonRecord, keys: readonly string[]): string | undefined {
  for (const key of keys) {
    const value = readString(record[key]);
    if (value !== undefined) return value;
  }
  return undefined;
}

export function resolveArray(record: JsonRecord, keys: readonly string[]): unknown[] | undefined {
  for (const key of keys) {
    if (Array.isArray(record[key])) return record[key] as unknown[];
  }
  return undefined;
}

export function parseTimestamp(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? Date.now() : parsed;
  }
  return Date.now();
}

/**
 * Like parseTimestamp but returns undefined instead of Date.now() on failure.
 * Used by api.ts where a missing timestamp should remain undefined.
 */
export function parseDateValue(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
}

export function normalizeRole(value: unknown): "user" | "assistant" {
  const role = readString(value)?.toLowerCase();
  if (role === "user" || role === "human") return "user";
  return "assistant";
}

export function createId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}
